import { useState, useEffect } from "react";
import { useParams } from "react-router-dom"
import { getEmail } from "../../api/axios"
import { day, nf, perc, getBrandClassName } from "../../utilities/Utils"
import useAuth from "../../hooks/useAuth";

const toNumber = (value) => Number(value ?? 0);

const calcPercentDisplay = (total, value) => {
  const totalNumber = toNumber(total);
  const valueNumber = toNumber(value);

  if (totalNumber <= 0 || valueNumber <= 0) {
    return "0%";
  }

  return perc(totalNumber, valueNumber);
};

const calcCtorDisplay = (clicks, opened) => {
  const openedNumber = toNumber(opened);
  const clicksNumber = toNumber(clicks);

  if (openedNumber <= 0 || clicksNumber <= 0) {
    return "0%";
  }

  return `${Math.round((clicksNumber / openedNumber) * 100)}%`;
};

const getEmailMetrics = (email = {}) => ({
  sent: {
    total: toNumber(email.sent),
    original: toNumber(email.sent_original),
    resend: toNumber(email.sent_resend),
  },
  opened: {
    total: toNumber(email.opened),
    original: toNumber(email.opened_original),
    resend: toNumber(email.opened_resend),
  },
  unsubscribed: {
    total: toNumber(email.unsubscribed),
    original: toNumber(email.unsubscribed_original),
    resend: toNumber(email.unsubscribed_resend),
  },
  clicks: {
    total: toNumber(email.clicks),
    original: toNumber(email.clicks_original),
    resend: toNumber(email.clicks_resend),
  },
  uniqueContacts: {
    total: toNumber(email.unique_contacts),
    original: toNumber(email.unique_contacts_original),
    resend: toNumber(email.unique_contacts_resend),
  },
});

const renderMetricCard = ({
  label,
  metrics,
  hasResend,
  totalExtra = null,
  originalExtra = null,
  resendExtra = null,
  format,
}) => {
  const formatValue = format ?? ((value) => nf(value));

  return (
    <article className="email-detail-metric">
      <span className="email-detail-metric__label">{label}</span>
      <div className={`metric-mini-stack${hasResend ? "" : " metric-mini-stack--single"}`}>
        {hasResend ? (
          <>
            <div className="metric-mini-stack__row">
              <div className="metric-mini-stack__label">Orig</div>
              <div className="metric-mini-stack__value">
                <span className="valor">{formatValue(metrics.original)}</span>
                {originalExtra}
              </div>
            </div>
            <div className="metric-mini-stack__row">
              <div className="metric-mini-stack__label">Rsnd</div>
              <div className="metric-mini-stack__value">
                <span className="valor">{formatValue(metrics.resend)}</span>
                {resendExtra}
              </div>
            </div>
          </>
        ) : null}
        <div className="metric-mini-stack__row metric-mini-stack__row--total">
          <div className="metric-mini-stack__label">{hasResend ? "Total" : "Orig"}</div>
          <div className="metric-mini-stack__value">
            <span className="valor">{formatValue(metrics.total)}</span>
            {totalExtra}
          </div>
        </div>
      </div>
    </article>
  );
};

const EmailInfo = (props) => {

  const { idEmail } = useParams();

  const [email, setEmail] = useState({});

  const { auth } = useAuth();

useEffect(() => {

    const fetchData = async () => {
      
      try {

        if(idEmail) {
          
          const data = await getEmail(idEmail, auth);
          setEmail(data.results);

        }
  
  
      } catch (error) {
  
        setEmail(null);
  
      }
  
    };
  
    fetchData();
      
  }, [idEmail, auth]);

  
  if(!email) {

    return (<div className="no_results">No se encontró el envío</div>);

  }

  const metrics = getEmailMetrics(email);
  const hasResend =
    metrics.sent.resend > 0 ||
    metrics.opened.resend > 0 ||
    metrics.unsubscribed.resend > 0 ||
    metrics.clicks.resend > 0 ||
    metrics.uniqueContacts.resend > 0 ||
    !!email.resend_subject;

  const ctorMetrics = {
    total: calcCtorDisplay(metrics.clicks.total, metrics.opened.total),
    original: calcCtorDisplay(metrics.clicks.original, metrics.opened.original),
    resend: calcCtorDisplay(metrics.clicks.resend, metrics.opened.resend),
  };

  const summaryCards = [
    {
      label: "Destinatarios",
      value: nf(metrics.sent.original),
      detail: metrics.sent.resend > 0 ? `${nf(metrics.sent.resend)} reenviados` : "Sin reenvío",
      accent: "red",
    },
    {
      label: "Open Rate",
      value: calcPercentDisplay(metrics.sent.total, metrics.opened.total),
      detail: `${nf(metrics.opened.total)} aperturas únicas`,
      accent: "gold",
    },
    {
      label: "CTR",
      value: calcPercentDisplay(metrics.sent.total, metrics.clicks.total),
      detail: `${nf(metrics.clicks.total)} clicks únicos`,
      accent: "ink",
    },
    {
      label: "CTOR",
      value: ctorMetrics.total,
      detail: `${nf(metrics.unsubscribed.total)} bajas`,
      accent: "sand",
    },
  ];


  return (

    <section className="emails-results email-detail-performance">
      <section className="emails-summary-shell">
        <div className="emails-summary-shell__intro">
          <span className="emails-eyebrow">Detalle del envío</span>
          <h2 className="emails-hero__title">{email.subject}</h2>
          {hasResend && email.resend_subject ? (
            <div className="metric-line metric-line--subject">
              <span className="metric-label">Reenvío:</span>
              <span className="metric-value">{email.resend_subject}</span>
            </div>
          ) : null}
          <div className="email-detail-meta">
            {email.id ? <span className="table-id-pill">{email.id}</span> : null}
            {email.brand_name ? (
              <span className={getBrandClassName(email.brand_name, "table-brand-label")}>
                {email.brand_name}
              </span>
            ) : null}
            {email.campaign_name ? <span>{email.campaign_name}</span> : null}
            <span>{String(email.journey_id) === "0" ? day(email.date_sent_from) : "Todos los dias"}</span>
          </div>
        </div>

        <div className="emails-summary-grid">
          {summaryCards.map((card) => (
            <article className={`emails-summary-card emails-summary-card--${card.accent}`} key={card.label}>
              <span className="emails-summary-card__label">{card.label}</span>
              <strong className="emails-summary-card__value">{card.value}</strong>
              <span className="emails-summary-card__detail">{card.detail}</span>
            </article>
          ))}
        </div>
      </section>

      <section className="emails-table-shell email-detail-metrics-shell">
        <div className="emails-table-shell__header">
          <div>
            <span className="emails-section-kicker">Performance</span>
            <h3 className="emails-section-title">Totales del envío</h3>
          </div>
        </div>
        <div className="email-detail-metrics-grid">
          {renderMetricCard({ label: "Envíos", metrics: metrics.sent, hasResend })}
          {renderMetricCard({ label: "Alcance", metrics: metrics.uniqueContacts, hasResend })}
          {renderMetricCard({
            label: "Aperturas",
            metrics: metrics.opened,
            hasResend,
            totalExtra: <span className="metric-extra">{calcPercentDisplay(metrics.sent.total, metrics.opened.total)}</span>,
            originalExtra: hasResend ? <span className="metric-extra">{calcPercentDisplay(metrics.sent.original, metrics.opened.original)}</span> : null,
            resendExtra: hasResend ? <span className="metric-extra">{calcPercentDisplay(metrics.sent.resend, metrics.opened.resend)}</span> : null,
          })}
          {renderMetricCard({
            label: "Clicks",
            metrics: metrics.clicks,
            hasResend,
            totalExtra: <span className="metric-extra">{calcPercentDisplay(metrics.sent.total, metrics.clicks.total)}</span>,
            originalExtra: hasResend ? <span className="metric-extra">{calcPercentDisplay(metrics.sent.original, metrics.clicks.original)}</span> : null,
            resendExtra: hasResend ? <span className="metric-extra">{calcPercentDisplay(metrics.sent.resend, metrics.clicks.resend)}</span> : null,
          })}
          {renderMetricCard({ label: "CTOR", metrics: ctorMetrics, hasResend, format: (value) => value })}
          {renderMetricCard({
            label: "Bajas",
            metrics: metrics.unsubscribed,
            hasResend,
            totalExtra: <span className="metric-extra">{calcPercentDisplay(metrics.sent.total, metrics.unsubscribed.total)}</span>,
            originalExtra: hasResend ? <span className="metric-extra">{calcPercentDisplay(metrics.sent.original, metrics.unsubscribed.original)}</span> : null,
            resendExtra: hasResend ? <span className="metric-extra">{calcPercentDisplay(metrics.sent.resend, metrics.unsubscribed.resend)}</span> : null,
          })}
        </div>
      </section>
    </section>
    
    
    )
}

export default EmailInfo
